import { useParams, useSearch } from '@tanstack/react-router';
import {
  SocialAccountType,
  useEngagementPostV2Query,
  useUpdateTtcmPostMutation,
  useUpdateEngagementPostV2Mutation,
  CampaignSocialMediaType,
  namedOperations,
  useUploadEngagementMetricsForGenReportMutation,
  useEngagementPostMissingMetricsDataQuery
} from '@anytag/graphql';
import { useQueryHelper } from '@anytag/shared-hooks';
import { useUserRoles } from '@anytag/auth';
import { EngagementPostForm, EngagementPostFormValues } from '@anytag/shared-organisms';
import { FormAction } from '@anytag/shared-types';
import { useEngagementDetailsContext } from '../engagement-details-context';

export const EngagementEditPost = () => {
  const { engagementId, engagement } = useEngagementDetailsContext();
  const params = useParams({ from: '/_private-routes/engagement/$id/posts/$postId' });
  const search = useSearch({ from: '/_private-routes/engagement/$id/posts/$postId' });
  const { isAdminStaff } = useUserRoles();
  const { t, enqueueSnackbar, navigate } = useQueryHelper();
  const { callUpdateTtcmPost } = useUpdateTtcmPostMutation({
    refetchQueries: [namedOperations.Query.AllEngagementPosts]
  });
  const { callUpdateEngagementPostV2 } = useUpdateEngagementPostV2Mutation({
    refetchQueries: [namedOperations.Query.AllEngagementPosts]
  });
  const { callUploadEngagementMetricsForGenReport } = useUploadEngagementMetricsForGenReportMutation({
    refetchQueries: [namedOperations.Query.EngagementPostsMissingMetricsForReport]
  });
  const { data, loading } = useEngagementPostV2Query({
    skip: !params.postId,
    fetchPolicy: 'network-only',
    variables: { pk: Number(params.postId) },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      goToPostsList();
    }
  });
  const postData = data?.engagementPostV2;

  const { data: postMetricsData, loading: fetchingPostMetricsData } = useEngagementPostMissingMetricsDataQuery({
    skip:
      !params.postId ||
      !isAdminStaff ||
      !(
        postData?.socialMedia &&
        [
          CampaignSocialMediaType.INSTAGRAM,
          CampaignSocialMediaType.INSTAGRAM_STORY,
          CampaignSocialMediaType.FACEBOOK,
          CampaignSocialMediaType.TWITTER,
          CampaignSocialMediaType.YOUTUBE,
          CampaignSocialMediaType.TIKTOK
        ].includes(postData?.socialMedia)
      ),
    variables: { postId: Number(params.postId) }
  });

  const postMissingMetricsData = postMetricsData?.engagementPostMissingMetricsData?.data;

  const goToPostsList = () => {
    navigate({ to: '/engagement/$id/posts', params, search, replace: true });
  };

  const handleSubmit: FormAction<EngagementPostFormValues>['onSubmit'] = async (values) => {
    if (!postData) return;

    try {
      if (values.isTtcmForm) {
        await callUpdateTtcmPost({
          variables: {
            input: {
              id: postData.id,
              content: values.content,
              thumbnailUrl: values.materialsUrl[0] || ''
            }
          }
        });
      } else {
        await callUpdateEngagementPostV2({
          variables: {
            input: {
              id: postData.id,
              content: values.content,
              postUrl: values.postUrl,
              campaignId: engagementId,
              insightUrls: values.insightUrls,
              materialsUrl: values.materialsUrl,
              influencerId: Number(values.influencerId),
              socialAccountId: Number(values.socialAccountId),
              insightDataAcquisition: values.insightDataAcquisition,
              socialAccountMedia: values.socialAccountMedia as SocialAccountType
            }
          }
        });

        if (postMissingMetricsData) {
          await callUploadEngagementMetricsForGenReport({
            variables: {
              input: {
                postId: postData.id,
                likes: values.likes ? Number(values.likes) : undefined,
                reach: values.reach ? Number(values.reach) : undefined,
                saves: values.saves ? Number(values.saves) : undefined,
                views: values.views ? Number(values.views) : undefined,
                shares: values.shares ? Number(values.shares) : undefined,
                comments: values.comments ? Number(values.comments) : undefined,
                dislikes: values.dislikes ? Number(values.dislikes) : undefined,
                impressions: values.impressions ? Number(values.impressions) : undefined,
                totalPlayTime: values.totalPlayTime ? Number(values.totalPlayTime) : undefined,
                avgWatchTimes: values.avgWatchTimes ? Number(values.avgWatchTimes) : undefined,
                detailExpands: values.detailExpands ? Number(values.detailExpands) : undefined,
                fullVideoViews: values.fullVideoViews ? Number(values.fullVideoViews) : undefined,
                avgPlayRate: values.avgPlayRate ? Number(values.avgPlayRate) / 100 : undefined,
                audienceRetention: values.audienceRetention ? Number(values.audienceRetention) / 100 : undefined,
                impressionsClickThroughRate: values.impressionsClickThroughRate
                  ? Number(values.impressionsClickThroughRate) / 100
                  : undefined
              }
            }
          });
        }
      }
      enqueueSnackbar(t('succeededInSave'), { variant: 'success' });
      goToPostsList();
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }
  };

  return (
    <EngagementPostForm
      data={postData}
      loading={loading || fetchingPostMetricsData}
      onSubmit={handleSubmit}
      onClose={goToPostsList}
      engagement={engagement}
      postMissingMetricsData={postMissingMetricsData}
    />
  );
};
